import { memo, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import { useFullscreen } from "@/lib/hooks/useFullscreen";
import { fmtInt } from "@/lib/format";

import { Icon } from "@/components/icons";
/** THE table (U2) — every data table renders through this, never a raw
 *  `<table className="tbl">`. Carries the measured-incident fixes so no future
 *  table can regress them:
 *
 * - `table-layout: fixed` + explicit `<colgroup>` widths: row mounts are
 *    row-local layout work. Auto layout (`max-content`) re-measured the ENTIRE
 *    table whenever content mounted — measured at 3–5 s of main-thread stall
 *    for a 150×40 window replace on the live tail.
 * - Sticky header row (column names never scroll away) and optional sticky
 *    first column, with the corner cell pinned on both axes.
 * - Memoized rows: with identity-stable `cols` (consumer's job — useMemo or
 *    module-const) and per-row object identity preserved across updates
 *    (React Query structural sharing / append-only buffers), only changed
 *    rows re-render and only new rows mount.
 * - Full-screen mode (⛶ / Esc) via useFullscreen.
 * - Cells ellipsize; pass `title` for the full value on hover.
 * - Optional `pageSize` pagination: CELLS ARE THE MOUNT BUDGET (law 7) —
 *    a 5000-row window × 45 columns is ~225k cells, a measured multi-second
 *    freeze. Any consumer whose row count is unbounded (History windows,
 *    search results) MUST page; the pager mounts one page, never the world.
 */
export type DataCol<T> = {
  key: string;
  label: ReactNode;
  /** px — fixed-layout column width (default 110) */
  width?: number;
  render: (row: T) => ReactNode;
  /** hover tooltip, e.g. the untruncated value */
  title?: (row: T) => string | undefined;
  mono?: boolean;
};

const W_DEFAULT = 110;
const cellStyle: CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const stickyCell: CSSProperties = { ...cellStyle, position: "sticky", left: 0, zIndex: 1, background: "var(--surface)", color: "var(--t-mute)" };

function RowInner<T>({ r, cols, stickyFirst, onClick }: {
  r: T; cols: DataCol<T>[]; stickyFirst?: boolean; onClick?: (row: T) => void;
}) {
  return (
    <tr style={onClick ? { cursor: "pointer" } : undefined} onClick={onClick ? () => onClick(r) : undefined}>
      {cols.map((c, i) => (
        <td key={c.key}
          className={c.mono ? "mono" : undefined}
          style={stickyFirst && i === 0 ? stickyCell : cellStyle}
          title={c.title?.(r)}>
          {c.render(r)}
        </td>
      ))}
    </tr>
  );
}
// memo() drops the generic — restore it with a cast (standard TS pattern)
const Row = memo(RowInner) as typeof RowInner;

export function DataTable<T>({
  rows, cols, rowKey, stickyFirst = false, maxHeight = 420,
  fullscreenable = false, onRowClick, empty, toolbar, pageSize,
}: {
  rows: T[];
  /** MUST be identity-stable across renders (module const or useMemo) or row
   *  memoization is defeated. */
  cols: DataCol<T>[];
  rowKey: (row: T) => string | number;
  stickyFirst?: boolean;
  maxHeight?: number;
  fullscreenable?: boolean;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  /** extra controls rendered left of the full-screen button */
  toolbar?: ReactNode;
  /** mount at most this many rows, with a ‹ › pager — REQUIRED when the row
   *  count is unbounded (cells are the mount budget) */
  pageSize?: number;
}) {
  const { full, setFull, overlayStyle } = useFullscreen();
  const tableWidth = useMemo(() => cols.reduce((a, c) => a + (c.width ?? W_DEFAULT), 0), [cols]);

  // pagination — page is CLAMPED, not reset, when rows shrink (a polling
  // consumer must not yank the user back to page 1 on every refetch)
  const [page, setPage] = useState(0);
  const pages = pageSize ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;
  const cur = Math.min(page, pages - 1);
  const view = useMemo(
    () => (pageSize ? rows.slice(cur * pageSize, (cur + 1) * pageSize) : rows),
    [rows, pageSize, cur],
  );

  if (rows.length === 0) {
    return empty !== undefined
      ? <>{empty}</>
      : <div className="panel panel-pad cap">No rows.</div>;
  }

  const headCell: CSSProperties = { ...cellStyle, position: "sticky", top: 0, zIndex: 2, background: "var(--well)" };
  const headCorner: CSSProperties = { ...headCell, left: 0, zIndex: 4 };

  return (
    <div style={overlayStyle}>
      {(toolbar || fullscreenable || pages > 1) && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 6 }}>
          {pages > 1 && pageSize && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--t-mute)" }}>
              <button className="btn ghost" disabled={cur === 0} onClick={() => setPage(cur - 1)} title="Previous page">‹</button>
              <span className="mono">
                {fmtInt(cur * pageSize + 1)}–{fmtInt(Math.min((cur + 1) * pageSize, rows.length))} of {fmtInt(rows.length)}
              </span>
              <button className="btn ghost" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)} title="Next page">›</button>
            </span>
          )}
          {toolbar}
          {fullscreenable && (
            <button className="btn ghost" onClick={() => setFull((f) => !f)}
              title={full ? "Exit full screen (Esc)" : "Expand the table to the whole window"}>
              <Icon name={full ? "close" : "fullscreen"} /> {full ? "Exit full screen" : "Full screen"}
            </button>
          )}
        </div>
      )}
      <div className="tblwrap" style={{ maxHeight: full ? "none" : maxHeight, flex: full ? 1 : undefined, minHeight: 0, overflow: "auto" }}>
        {/* width:100% + minWidth:sum - narrow tables STRETCH to fill the panel
            (fixed layout distributes the extra across columns); wide ones keep
            their computed width and scroll horizontally. Layout stays fixed =
            row mounts never trigger a whole-table re-measure. */}
        <table className="tbl" style={{ tableLayout: "fixed", width: "100%", minWidth: tableWidth }}>
          <colgroup>
            {cols.map((c) => <col key={c.key} style={{ width: c.width ?? W_DEFAULT }} />)}
          </colgroup>
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th key={c.key} style={stickyFirst && i === 0 ? headCorner : headCell}
                  title={typeof c.label === "string" ? c.key : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((r) => (
              <Row key={rowKey(r)} r={r} cols={cols} stickyFirst={stickyFirst} onClick={onRowClick} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
